import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, FileText, CheckSquare, Square, Receipt, CreditCard, RefreshCw, AlertCircle } from 'lucide-react';
import api from '../utils/api';
import useGstRate from '../utils/useGstRate';
import toast from 'react-hot-toast';

const TYPE_COLOR = {
  'ITR Filing':  'bg-blue-100 text-blue-700',
  'GST Return':  'bg-orange-100 text-orange-700',
  'TDS Return':  'bg-purple-100 text-purple-700',
  'Audit':       'bg-red-100 text-red-700',
  'ROC Filing':  'bg-indigo-100 text-indigo-700',
  'ESI-PF':      'bg-teal-100 text-teal-700',
  'Meeting':     'bg-cyan-100 text-cyan-700',
  'Task':        'bg-green-100 text-green-700',
};

const FMT = v => v == null ? '—' : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

function InvoicePreviewModal({ invoice, client, onClose, gstRate }) {
  const today = new Date().toLocaleDateString('en-IN');
  const svcs = invoice.services || [];
  const subtotal = svcs.reduce((s, i) => s + (parseFloat(i.fee_amount) || 0), 0);
  const gst = invoice.include_gst ? subtotal * (gstRate / 100) : 0;
  const total = subtotal + gst;

  const confirm = async () => {
    try {
      const r = await api.post('/work-ledger/generate-invoice', {
        client_id: client.id,
        items: svcs.map(i => ({ ...i, fee_amount: i.fee_amount || 0 })),
        invoice_date: invoice.invoice_date,
        due_date: invoice.due_date,
        notes: invoice.notes,
        include_gst: invoice.include_gst,
      });
      toast.success(`Invoice ${r.data.invoice_number} created`);
      onClose(true);
    } catch (e) { toast.error(e?.response?.data?.error || 'Failed'); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => onClose(false)}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Invoice header */}
        <div className="bg-[#1e3a5f] text-white p-6 rounded-t-2xl text-center">
          <div className="font-bold text-lg">KRISHNA UDAY & ASSOCIATES</div>
          <div className="text-blue-200 text-sm">Chartered Accountants, Hyderabad</div>
          <div className="text-blue-300 text-xs mt-1">22-102/160, RK Nagar, Hyderabad - 500047</div>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex justify-between text-sm">
            <div>
              <div className="font-bold text-gray-700 mb-1">Invoice Preview</div>
              <div className="text-gray-500">Date: {invoice.invoice_date || today}</div>
              {invoice.due_date && <div className="text-gray-500">Due: {invoice.due_date}</div>}
            </div>
            <div className="text-right text-sm">
              <div className="font-semibold text-gray-700">Bill To:</div>
              <div className="text-gray-700">{client.name}</div>
              {client.address_registered && <div className="text-gray-400 text-xs max-w-[150px] text-right">{client.address_registered}</div>}
              {client.pan && <div className="text-gray-500 font-mono text-xs">PAN: {client.pan}</div>}
            </div>
          </div>

          <table className="w-full text-sm border-collapse">
            <thead><tr className="border-b-2 border-gray-200">
              <th className="text-left py-2 text-xs text-gray-500">#</th>
              <th className="text-left py-2 text-xs text-gray-500">Description</th>
              <th className="text-right py-2 text-xs text-gray-500">Amount</th>
            </tr></thead>
            <tbody>
              {svcs.map((item, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="py-1.5 text-gray-500 text-xs">{i + 1}</td>
                  <td className="py-1.5 text-gray-700">{item.work_description}</td>
                  <td className="py-1.5 text-right font-medium">₹{FMT(item.fee_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="border-t-2 border-gray-200 pt-3 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span className="font-medium">₹{FMT(subtotal)}</span></div>
            {invoice.include_gst && <>
              <div className="flex justify-between text-gray-400 text-xs"><span>CGST @9%</span><span>₹{FMT(gst / 2)}</span></div>
              <div className="flex justify-between text-gray-400 text-xs"><span>SGST @9%</span><span>₹{FMT(gst / 2)}</span></div>
            </>}
            <div className="flex justify-between font-bold text-[#1e3a5f] text-base border-t border-gray-200 pt-2"><span>Total</span><span>₹{FMT(total)}</span></div>
          </div>

          <div className="flex gap-3 pt-2">
            <button onClick={confirm} className="flex-1 bg-[#f0a500] hover:bg-[#d4920a] text-white py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-2">
              <CheckSquare size={15} /> Confirm & Save Invoice
            </button>
            <button onClick={() => onClose(false)} className="btn-secondary px-5">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PaymentModal({ invoice, onClose }) {
  const [form, setForm] = useState({
    amount: invoice ? (invoice.total - (invoice.paid_amount || 0)).toFixed(0) : '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_mode: 'Bank Transfer',
    bank_name: '',
    reference_number: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const save = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post('/work-ledger/payment', { ...form, invoice_id: invoice.id, client_id: invoice.client_id });
      toast.success(`Payment recorded — ${r.data.receipt_number}. Status: ${r.data.new_status}`);
      onClose(true);
    } catch (err) { toast.error(err?.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  };

  const outstanding = invoice ? (invoice.total - (invoice.paid_amount || 0)) : 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => onClose(false)}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="bg-[#1e3a5f] text-white px-6 py-4 rounded-t-2xl flex items-center gap-2">
          <CreditCard size={16} className="text-[#f0a500]" />
          <div>
            <div className="font-semibold text-sm">Record Payment</div>
            {invoice && <div className="text-blue-200 text-xs">{invoice.invoice_number} · Outstanding: ₹{FMT(outstanding)}</div>}
          </div>
        </div>
        <form onSubmit={save} className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs font-medium text-gray-500">Amount Received (₹) *</label>
              <input type="number" required value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} className="input-field mt-1" min={1} max={outstanding} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Payment Date *</label>
              <input type="date" required value={form.payment_date} onChange={e => setForm(f => ({ ...f, payment_date: e.target.value }))} className="input-field mt-1" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Mode</label>
              <select value={form.payment_mode} onChange={e => setForm(f => ({ ...f, payment_mode: e.target.value }))} className="input-field mt-1">
                {['Cash','Cheque','NEFT','RTGS','UPI','Bank Transfer'].map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Bank / UPI</label>
              <input value={form.bank_name} onChange={e => setForm(f => ({ ...f, bank_name: e.target.value }))} className="input-field mt-1" placeholder="HDFC Bank / UPI ID" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Reference / UTR</label>
              <input value={form.reference_number} onChange={e => setForm(f => ({ ...f, reference_number: e.target.value }))} className="input-field mt-1" placeholder="UTR / Cheque no" />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium text-gray-500">Notes</label>
              <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="input-field mt-1" />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving} className="flex-1 btn-primary py-2.5">{saving ? 'Saving…' : 'Save Payment'}</button>
            <button type="button" onClick={() => onClose(false)} className="btn-secondary px-5">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function WorkLedgerPage() {
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [works, setWorks] = useState([]);
  const [ledger, setLedger] = useState({ entries: [], outstanding: 0 });
  const [invoices, setInvoices] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [fees, setFees] = useState({});
  const [includeGst, setIncludeGst] = useState(true);
  const gstRate = useGstRate();
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [paymentInvoice, setPaymentInvoice] = useState(null);
  const [tab, setTab] = useState('unbilled');

  const load = useCallback(() => {
    if (!clientId) return;
    api.get(`/clients/${clientId}`).then(r => setClient(r.data)).catch(() => {});
    api.get(`/work-ledger/${clientId}`).then(r => setWorks(r.data)).catch(() => {});
    api.get(`/work-ledger/${clientId}/ledger`).then(r => setLedger(r.data)).catch(() => {});
    api.get('/billing', { params: { client_id: clientId } }).then(r => setInvoices(r.data)).catch(() => {});
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const unbilled = works.filter(w => !w.is_billed);
  const billed = works.filter(w => w.is_billed);

  const toggle = id => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(s => s.size === unbilled.length ? new Set() : new Set(unbilled.map(w => w.id)));

  const selectedWorks = unbilled.filter(w => selected.has(w.id));
  const subtotal = selectedWorks.reduce((s, w) => s + parseFloat(fees[w.id] ?? w.default_fee ?? 0), 0);
  const gstAmt = includeGst ? subtotal * (gstRate / 100) : 0;
  const totalAmt = subtotal + gstAmt;

  const handleGenerate = () => {
    if (selected.size === 0) { toast.error('Select at least one work item'); return; }
    setShowPreview(true);
  };

  const invoiceData = {
    include_gst: includeGst,
    invoice_date: invoiceDate,
    due_date: dueDate,
    services: selectedWorks.map(w => ({ ...w, fee_amount: parseFloat(fees[w.id] ?? w.default_fee ?? 0) })),
  };

  if (!client) return <div className="flex items-center justify-center h-64 text-gray-400">Loading…</div>;

  return (
    <div className="space-y-5 max-w-5xl">
      <div className="flex items-center gap-3">
        <Link to={`/clients/${clientId}`} className="text-gray-400 hover:text-[#1e3a5f]"><ArrowLeft size={20} /></Link>
        <div>
          <h1 className="text-xl font-bold text-[#1e3a5f]">Work & Billing — {client.name}</h1>
          <div className="text-xs text-gray-400">{client.client_code} · {client.entity_type}</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className={`px-4 py-2 rounded-xl text-sm font-semibold ${ledger.outstanding > 0 ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>
            Outstanding: ₹{FMT(Math.abs(ledger.outstanding))} {ledger.outstanding > 0 ? 'Dr' : ledger.outstanding < 0 ? 'Cr' : ''}
          </div>
          <button onClick={load} className="p-2 text-gray-400 hover:text-gray-600"><RefreshCw size={16} /></button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {[['unbilled', `Unbilled Work (${unbilled.length})`], ['ledger', 'Client Ledger'], ['invoices', `Invoices (${invoices.length})`]].map(([t, l]) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t ? 'border-[#1e3a5f] text-[#1e3a5f]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{l}</button>
        ))}
      </div>

      {/* UNBILLED TAB */}
      {tab === 'unbilled' && (
        <div className="space-y-4">
          {unbilled.length === 0 ? (
            <div className="card text-center py-10 text-gray-400">
              <FileText size={36} className="mx-auto mb-2 text-gray-300" />
              <div className="font-medium">No unbilled completed work</div>
              <div className="text-xs mt-1">Work completed and filed will appear here for invoicing.</div>
            </div>
          ) : (
            <>
              {/* Feature 5: read-only summary — invoicing moved to Billing → Unbilled */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-2.5 text-sm text-blue-700 flex items-center gap-2">
                <AlertCircle size={15} className="flex-shrink-0" />
                <span>Read-only summary. Generate invoices and record payments from <Link to="/billing" className="underline font-medium">Billing → Unbilled Work</Link>.</span>
              </div>
              <div className="card overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-2 bg-gray-50 border-b text-xs font-medium text-gray-500">
                  <span className="w-28">Work Type</span>
                  <span className="flex-1">Description</span>
                  <span className="w-24">Period</span>
                  <span className="w-24">Completed</span>
                  <span className="w-28 text-right">Suggested Fee</span>
                </div>
                {unbilled.map(w => (
                  <div key={`${w.work_type}-${w.id}`} className="flex items-center gap-3 px-4 py-2.5 border-b last:border-0">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium w-28 text-center ${TYPE_COLOR[w.work_type] || 'bg-gray-100 text-gray-600'}`}>{w.work_type}</span>
                    <span className="flex-1 text-sm text-gray-700 truncate">{w.work_description}</span>
                    <span className="w-24 text-xs text-gray-500 truncate">{w.work_period}</span>
                    <span className="w-24 text-xs text-gray-400">{w.completed_date ? new Date(w.completed_date + 'T00:00').toLocaleDateString('en-IN') : '—'}</span>
                    <span className="w-28 text-right text-sm text-gray-500">₹{FMT(w.default_fee ?? 0)}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Previously billed */}
          {billed.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-500 mb-2">Already Billed ({billed.length})</h3>
              <div className="card divide-y opacity-70">
                {billed.map(w => (
                  <div key={w.id} className="flex items-center gap-3 px-4 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TYPE_COLOR[w.work_type] || 'bg-gray-100 text-gray-600'}`}>{w.work_type}</span>
                    <span className="flex-1 text-sm text-gray-500 truncate">{w.work_description}</span>
                    <span className="text-xs text-green-600 font-medium">✓ Billed</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* LEDGER TAB */}
      {tab === 'ledger' && (
        <div className="space-y-3">
          {ledger.entries?.length === 0 ? (
            <div className="card text-center py-10 text-gray-400">No ledger entries yet.</div>
          ) : (
            <div className="card overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-2 bg-gray-50 border-b text-xs font-medium text-gray-500">
                <span className="w-24">Date</span>
                <span className="flex-1">Particulars</span>
                <span className="w-20 text-right">Dr (₹)</span>
                <span className="w-20 text-right">Cr (₹)</span>
                <span className="w-28 text-right">Balance</span>
              </div>
              {ledger.entries.map((e, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-2.5 border-b last:border-0 text-sm hover:bg-gray-50">
                  <span className="w-24 text-xs text-gray-500">{e.date ? new Date(e.date + 'T00:00').toLocaleDateString('en-IN') : '—'}</span>
                  <span className="flex-1 text-gray-700">{e.particulars}</span>
                  <span className="w-20 text-right text-red-600 font-medium">{e.debit ? `₹${FMT(e.debit)}` : '—'}</span>
                  <span className="w-20 text-right text-green-600 font-medium">{e.credit ? `₹${FMT(e.credit)}` : '—'}</span>
                  <span className={`w-28 text-right font-semibold text-xs ${e.balance >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                    ₹{FMT(Math.abs(e.balance))} {e.balance_type}
                  </span>
                </div>
              ))}
              <div className="px-4 py-3 bg-gray-50 border-t flex justify-between items-center">
                <span className="text-sm font-semibold text-gray-700">Closing Balance</span>
                <span className={`text-base font-bold ${ledger.outstanding > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  ₹{FMT(Math.abs(ledger.outstanding))} {ledger.outstanding > 0 ? 'Dr (Outstanding)' : ledger.outstanding < 0 ? 'Cr (Advance)' : 'Nil'}
                </span>
              </div>
            </div>
          )}

          {/* Outstanding invoices with Pay button */}
          {invoices.filter(i => ['Sent','Overdue','Partial'].includes(i.status)).map(inv => (
            <div key={inv.id} className="card flex items-center gap-3 bg-red-50 border border-red-200">
              <AlertCircle size={16} className="text-red-500 flex-shrink-0" />
              <div className="flex-1">
                <div className="font-medium text-sm text-gray-800">{inv.invoice_number}</div>
                <div className="text-xs text-gray-500">{inv.status} · Due: {inv.due_date || '—'}</div>
              </div>
              <div className="text-right">
                <div className="font-bold text-red-600">₹{FMT(inv.total - (inv.paid_amount || 0))}</div>
                <div className="text-xs text-gray-400">of ₹{FMT(inv.total)}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* INVOICES TAB */}
      {tab === 'invoices' && (
        <div className="space-y-2">
          {invoices.length === 0 ? (
            <div className="card text-center py-10 text-gray-400">No invoices yet. Generate one from Billing → Unbilled Work.</div>
          ) : invoices.map(inv => (
            <div key={inv.id} className="card flex items-center gap-4">
              <div className="flex-1">
                <div className="font-semibold text-gray-800">{inv.invoice_number}</div>
                <div className="text-xs text-gray-400 mt-0.5">{inv.invoice_date} · {Array.isArray(inv.services) ? inv.services.map(s => s.name).join(', ') : '—'}</div>
              </div>
              <div className="text-center">
                <div className="text-sm font-bold text-gray-700">₹{FMT(inv.total)}</div>
                <div className="text-xs text-gray-400">Total</div>
              </div>
              <div className="text-center">
                <div className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                  inv.status === 'Paid' ? 'bg-green-100 text-green-700' :
                  inv.status === 'Partial' ? 'bg-yellow-100 text-yellow-700' :
                  inv.status === 'Overdue' ? 'bg-red-100 text-red-700' :
                  'bg-gray-100 text-gray-600'}`}>{inv.status}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
